'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import { pageChips as pageChipsFor } from '@/lib/assistant/page-context';
import { composerBlock } from '@/lib/assistant/session';
import type { AgentBlock } from '@/lib/api/types';

import { useAssistant } from './assistant-provider';
import { ChartBlock } from './blocks/chart-block';
import { ConfirmationCard, type ResolveKind } from './blocks/confirmation-card';
import { MarkdownBlock } from './blocks/markdown-block';
import { SuggestionChips } from './blocks/suggestion-chips';
import { ToolActivityBlock } from './blocks/tool-activity';
import { AssistantEmptyState } from './empty-state';

/**
 * The transcript, in arrival order.
 *
 * Exported separately from the view so it can be tested without a provider —
 * and because a replayed conversation and a live one are the same list.
 * Task 10 adds `onResolve`/`busy` props and a `case 'confirmation'`; Task 11
 * adds `onPick`, `picksDisabled` and a `case 'suggestions'`. Both block types
 * fall through harmlessly (rendered as nothing) until then, rather than
 * crashing on an unhandled variant.
 *
 * `busy` and `picksDisabled` are deliberately separate props, not one shared
 * flag: `busy` means "a resolution is in flight" and gates the confirmation
 * card's Apply/Cancel (Task 10, unchanged here), while `picksDisabled` gates
 * the suggestion chips. They cannot be the same value — the backend charges
 * quota for a new message (`POST .../messages`, which is what clicking a chip
 * sends) but never for resolving an already-proposed action
 * (`backend/src/api/routers/agent.py` has no quota check anywhere in
 * `apply_action`/`cancel_action`/apply-all). Reusing `busy` for both once
 * disabled Apply and Cancel on a spent allowance too, stranding an
 * already-approved change — and blocking even *cancelling* it — until the
 * next reset, on a request the backend would have honoured.
 *
 * A write turn can finish as `turn_started → confirmation_required →
 * turn_done` with no assistant text at all, and a reconnect could in
 * principle hand back an `assistant_text` block whose `text` is empty. Either
 * way this must never draw an empty message bubble above the card that
 * follows it, so an all-whitespace `assistant_text` block renders nothing.
 */
export function BlockList({
  blocks,
  onNavigate,
  onResolve,
  onPick,
  busy = false,
  picksDisabled = false,
}: {
  blocks: AgentBlock[];
  onNavigate?: () => void;
  onResolve?: (kind: ResolveKind, id: string) => void;
  onPick?: (option: string) => void;
  busy?: boolean;
  picksDisabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'user_text':
            return (
              <p
                key={index}
                className="ml-auto max-w-[85%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground"
              >
                {block.text}
              </p>
            );
          case 'assistant_text':
            if (!block.text.trim()) return null;
            return <MarkdownBlock key={index} text={block.text} onNavigate={onNavigate} />;
          case 'tool_activity':
            return (
              <ToolActivityBlock key={index} tool={block.tool} ok={block.ok} summary={block.summary} />
            );
          case 'chart':
            return (
              <ChartBlock
                key={block.chart_id}
                kind={block.kind}
                title={block.title}
                x_key={block.x_key}
                y_keys={block.y_keys}
                rows={block.rows}
              />
            );
          case 'confirmation':
            return (
              <ConfirmationCard
                key={index}
                actions={block.actions}
                busy={busy}
                onResolve={(kind, id) => onResolve?.(kind, id)}
              />
            );
          case 'suggestions':
            return (
              <SuggestionChips
                key={index}
                options={block.options}
                disabled={picksDisabled}
                onPick={(option) => onPick?.(option)}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

export function ConversationView() {
  const { status, turn, streaming, notice, error, minimize, resolve, send } = useAssistant();
  const bottom = useRef<HTMLDivElement>(null);
  // Hoisted here, not called inside the JSX below, so the hooks order stays
  // fixed regardless of which branch renders — a hook inside JSX is a
  // hooks-order violation the lint rule rejects.
  const pathname = usePathname();

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [turn.blocks, streaming]);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
      {/* Polite, not assertive: a screen reader should hear the finished answer,
          not every one-word delta as it lands. */}
      <div aria-live="polite" aria-busy={streaming}>
        {turn.blocks.length === 0 ? (
          <AssistantEmptyState
            suggestions={status?.suggestions ?? []}
            pageChips={pageChipsFor(pathname ?? '')}
            disabled={streaming || Boolean(composerBlock(status))}
            onPick={(prompt) => void send(prompt)}
          />
        ) : (
          <BlockList
            blocks={turn.blocks}
            onNavigate={minimize}
            onResolve={(kind, id) => void resolve(kind, id)}
            onPick={(option) => void send(option)}
            busy={streaming}
            picksDisabled={streaming || Boolean(composerBlock(status))}
          />
        )}
      </div>

      {notice && <p className="mt-3 text-xs text-muted-foreground">{notice}</p>}

      {error && (
        <p className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
          {error.message}
        </p>
      )}

      <div ref={bottom} />
    </div>
  );
}
